import { gridClient } from '@/utils/apiUtil'

/**
 * Free trial enrolment — `PATCH /campaign/free-trial/enroll/:campaignId/:customerId`
 * from the "Grid: Campaigns" Postman collection.
 *
 * That collection is documented as `SUPER`-role only ("intended for internal
 * platform administrators"), so a customer's own token is expected to come back
 * `403` until the backend exposes a customer-scoped self-enrolment route. The
 * outcome is therefore reported rather than thrown, and callers must treat
 * anything but `enrolled`/`already-enrolled` as "this customer has no trial".
 */
export type FreeTrialEnrollmentOutcome =
  | 'enrolled'
  | 'already-enrolled'
  | 'forbidden'
  | 'ineligible'

type EnrollArgs = {
  campaignId: string
  customerId: string
}

export const enrollInFreeTrialRequest = async (
  { campaignId, customerId }: EnrollArgs,
): Promise<FreeTrialEnrollmentOutcome> => {
  const { status } = await gridClient.patch(
    `/campaign/free-trial/enroll/${campaignId}/${customerId}`,
    undefined,
    {
      // Read the status here so `409 already enrolled` can be told apart from a
      // real failure — the shared interceptor normalizes errors and drops it.
      // 401 is deliberately left to throw so that interceptor can still refresh
      // the token and retry the request.
      validateStatus: code => code < 500 && code !== 401,
    },
  )

  if (status === 200 || status === 201) {
    return 'enrolled'
  }
  if (status === 409) {
    return 'already-enrolled'
  }
  if (status === 403) {
    return 'forbidden'
  }

  return 'ineligible'
}

export const isEnrolled = (outcome: FreeTrialEnrollmentOutcome): boolean => (
  outcome === 'enrolled' || outcome === 'already-enrolled'
)

/**
 * Free trial terms — `PATCH /customer/:customerId/free-trial/terms/accept` from
 * the "Grid: Verify" Postman collection.
 *
 * Acceptance is recorded against the customer rather than the user, and the
 * backend refuses `enrollCustomerInFreeTrialCampaign` until the flag is set, so
 * this has to land before enrolment is attempted.
 *
 * Terms that have to be signed are only accepted with the `submitterId` of the
 * completed signing form, which the backend checks with DocuSeal itself.
 *
 * @param customerId - The customer accepting the terms.
 * @param campaignId - The deep link's campaign, which decides the trial type.
 * @param submitterId - The signing form's submitter, for terms that are signed.
 */
export const acceptFreeTrialTermsRequest = async (
  customerId: string,
  campaignId?: string,
  submitterId?: number,
): Promise<void> => {
  await gridClient.patch(`/customer/${customerId}/free-trial/terms/accept`, undefined, {
    params: campaignId || submitterId
      ? { campaignId, submitterId }
      : undefined,
  })
}

export type FreeTrialTermsSigningSession = {
  signatureRequired: boolean
  embedSrc?: string
  submitterId?: number
}

/**
 * Opens the DocuSeal signing form for the agreement — `POST
 * /customer/:customerId/free-trial/terms/signing`.
 *
 * Only some trial types have to be signed (Head-to-Head today). The rest come
 * back `signatureRequired: false` and are read from the PDF instead.
 *
 * @param customerId - The customer entering the trial.
 * @param campaignId - The deep link's campaign, which decides the trial type.
 * @returns The form to embed, and the submitter to accept with once it is signed.
 */
export const startFreeTrialTermsSigningRequest = async (
  customerId: string,
  campaignId?: string,
): Promise<FreeTrialTermsSigningSession> => {
  const { data, status } = await gridClient.post<FreeTrialTermsSigningSession>(
    `/customer/${customerId}/free-trial/terms/signing`,
    undefined,
    {
      params: campaignId
        ? { campaignId }
        : undefined,
      // A backend without this route predates signed terms and accepts on the
      // PDF alone, so its 404 means "nothing to sign" rather than a failure.
      // Without this the terms step breaks wherever the frontend ships first.
      validateStatus: code => (code >= 200 && code < 300) || code === 404,
    },
  )

  return status === 404
    ? { signatureRequired: false }
    : data
}

/**
 * The agreement the customer has to read — `GET
 * /customer/:customerId/free-trial/terms/pdf`.
 *
 * The PDF is rendered per customer (their name and an effective date are
 * written into it), so it is fetched with the bearer token as a binary body
 * rather than linked to directly.
 */
export const getFreeTrialTermsPdfRequest = async (customerId: string, campaignId?: string): Promise<Blob> => {
  const { data } = await gridClient.get<Blob>(
    `/customer/${customerId}/free-trial/terms/pdf`,
    {
      responseType: 'blob',
      // The shared headers ask for JSON, which some gateways honour by refusing
      // the binary body outright.
      headers: { Accept: '*/*' },
      params: campaignId
        ? { campaignId }
        : undefined,
    },
  )

  // An embed only renders inline when the blob claims to be a PDF, and the
  // content type does not always survive the trip through the proxy.
  return data.type === 'application/pdf'
    ? data
    : new Blob([data], { type: 'application/pdf' })
}
